import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { LocalizedLink } from "@/components/LocalizedLink";
import { useLanguage } from "@/contexts/LanguageContext";
import { Cookie, Settings, ChevronDown, ChevronUp } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const COOKIE_CONSENT_KEY = "cookie_consent";
const COOKIE_PREFERENCES_KEY = "cookie_preferences";

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

const DEFAULT_PREFERENCES: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
  functional: false,
};

export function getCookiePreferences(): CookiePreferences {
  try {
    const stored = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // ignore
  }
  return DEFAULT_PREFERENCES;
}

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(DEFAULT_PREFERENCES);
  const { t } = useLanguage();

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      setIsVisible(true);
    } else {
      setPreferences(getCookiePreferences());
    }
  }, []);

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "custom");
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    setIsVisible(false);
  };

  const acceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
    };
    savePreferences(allAccepted);
  };

  const acceptNecessaryOnly = () => {
    savePreferences(DEFAULT_PREFERENCES);
  };

  const saveCustomPreferences = () => {
    savePreferences(preferences);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-background/95 backdrop-blur-sm border-t border-border shadow-lg animate-in slide-in-from-bottom-5 duration-300">
      <div className="container mx-auto max-w-4xl">
        {/* Main banner */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
            <div className="flex items-start gap-3">
              <Cookie className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm text-foreground">
                  {t("cookies.message", "Táto stránka používa cookies na zlepšenie vášho zážitku a analýzu návštevnosti.")}
                </p>
                <LocalizedLink 
                  to="/ochrana-sukromia" 
                  className="text-sm text-primary hover:underline font-medium"
                >
                  {t("cookies.privacy_link", "Viac o ochrane osobných údajov →")}
                </LocalizedLink>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSettings(!showSettings)}
                className="text-muted-foreground gap-1"
              >
                <Settings className="w-4 h-4" />
                {t("cookies.settings", "Nastavenia")}
                {showSettings ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={acceptNecessaryOnly}
              >
                {t("cookies.decline", "Len nevyhnutné")}
              </Button>
              <Button
                size="sm"
                onClick={acceptAll}
              >
                {t("cookies.accept_all", "Prijať všetky")}
              </Button>
            </div>
          </div>

          {/* Expanded settings */}
          {showSettings && (
            <div className="border-t border-border pt-4 mt-2 animate-in fade-in-0 slide-in-from-top-2 duration-200">
              <div className="grid gap-4 md:grid-cols-2">
                {/* Necessary cookies */}
                <div className="flex items-start justify-between gap-3 p-3 bg-muted/30 rounded-lg">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-foreground">
                      {t("cookies.necessary_title", "Nevyhnutné cookies")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t("cookies.necessary_desc", "Potrebné pre základné fungovanie stránky. Nedajú sa vypnúť.")}
                    </p>
                  </div>
                  <Switch checked={true} disabled className="opacity-50" />
                </div>

                {/* Analytics cookies */}
                <div className="flex items-start justify-between gap-3 p-3 bg-muted/30 rounded-lg">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-foreground">
                      {t("cookies.analytics_title", "Analytické cookies")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t("cookies.analytics_desc", "Pomáhajú nám pochopiť, ako návštevníci používajú stránku.")}
                    </p>
                  </div>
                  <Switch 
                    checked={preferences.analytics} 
                    onCheckedChange={(checked) => setPreferences(p => ({ ...p, analytics: checked }))}
                  />
                </div>

                {/* Functional cookies */}
                <div className="flex items-start justify-between gap-3 p-3 bg-muted/30 rounded-lg">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-foreground">
                      {t("cookies.functional_title", "Funkčné cookies")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t("cookies.functional_desc", "Umožňujú rozšírené funkcie ako zapamätanie preferencií.")}
                    </p>
                  </div>
                  <Switch 
                    checked={preferences.functional} 
                    onCheckedChange={(checked) => setPreferences(p => ({ ...p, functional: checked }))}
                  />
                </div>

                {/* Marketing cookies */}
                <div className="flex items-start justify-between gap-3 p-3 bg-muted/30 rounded-lg">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-foreground">
                      {t("cookies.marketing_title", "Marketingové cookies")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t("cookies.marketing_desc", "Používané na zobrazovanie relevantnej reklamy.")}
                    </p>
                  </div>
                  <Switch 
                    checked={preferences.marketing} 
                    onCheckedChange={(checked) => setPreferences(p => ({ ...p, marketing: checked }))}
                  />
                </div>
              </div>

              <div className="flex justify-end mt-4">
                <Button size="sm" onClick={saveCustomPreferences}>
                  {t("cookies.save_preferences", "Uložiť nastavenia")}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Export a function to open cookie settings from anywhere
export function openCookieSettings() {
  localStorage.removeItem(COOKIE_CONSENT_KEY);
  window.location.reload();
}
